import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useInstructorModules } from '@/hooks/useModules'
import { useAuth } from '@/hooks/useAuth'
import type { Module } from '@/types/module'

type ModuleStatus = 'all' | 'published' | 'draft'

interface ModuleWithCourse extends Module {
    courseName?: string
}

export const InstructorModulesPage: React.FC = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [statusFilter, setStatusFilter] = useState<ModuleStatus>('all')

    const { data: modulesData, isLoading, error } = useInstructorModules(user?.id)

    const modules = useMemo(() => {
        if (!modulesData) return []
        return (modulesData as ModuleWithCourse[]).map((module) => ({
            id: module.id,
            title: module.title,
            lessonCount: module.videoCount + module.quizCount,
            status: module.isDraft ? 'draft' : 'published',
            courseName: module.courseName || 'Unknown Course',
        }))
    }, [modulesData])

    const filteredModules = useMemo(() => {
        return modules.filter((m) => statusFilter === 'all' || m.status === statusFilter)
    }, [modules, statusFilter])

    return (
        <div className="max-w-[1600px] mx-auto">
            <div className="pb-8 mb-8 border-b border-slate-200 dark:border-white/10">
                <PageHeader
                    title="My Modules"
                    subtitle="All modules assigned to you across courses"
                />
            </div>

            <div className="px-1">
                {/* Filters */}
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-xl mb-8 w-fit">
                    {(['all', 'published', 'draft'] as ModuleStatus[]).map((status) => (
                        <button
                            key={status}
                            onClick={() => setStatusFilter(status)}
                            className={`px-5 py-2 rounded-lg text-xs font-bold transition-all ${statusFilter === status
                                    ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                                }`}
                            disabled={isLoading}
                        >
                            {status === 'all' ? 'Semua' : status === 'published' ? 'Terbit' : 'Draft'}
                        </button>
                    ))}
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="text-center py-16">
                        <div className="animate-spin inline-block size-8 border-4 border-slate-100 border-t-primary rounded-full mb-4" />
                        <p className="text-sm font-bold text-slate-500">Memuat modul...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-6 text-center">
                        <span className="material-symbols-outlined text-red-400 text-3xl mb-2">error</span>
                        <p className="text-red-500 font-bold">Gagal memuat modul</p>
                        <p className="text-red-400/70 text-sm mt-1">{error.message}</p>
                    </div>
                )}

                {/* Modules List */}
                {!isLoading && !error && (
                    <div className="bg-white dark:bg-slate-900 rounded-[24px] border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
                        {filteredModules.length === 0 ? (
                            <div className="px-6 py-16 text-center text-slate-500 dark:text-slate-400 font-bold">
                                Tidak ada modul yang ditemukan
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100 dark:divide-white/5">
                                {filteredModules.map((module) => (
                                    <div
                                        key={module.id}
                                        className="flex items-center justify-between gap-6 px-6 py-5 hover:bg-slate-50/50 dark:hover:bg-white/[0.01] transition-all group"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="size-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                                                <span className="material-symbols-outlined text-primary text-[22px]">folder</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-primary transition-colors truncate tracking-tight">
                                                    {module.title}
                                                </h3>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                                                    {module.courseName}
                                                </p>
                                                <div className="flex items-center gap-3 mt-2">
                                                    <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                                        <span className="material-symbols-outlined text-[14px]">play_circle</span>
                                                        <span className="font-bold">{module.lessonCount} materi</span>
                                                    </div>
                                                    <span className={`inline-flex px-2.5 py-0.5 rounded-lg text-[11px] font-black tracking-wide ${module.status === 'published'
                                                            ? 'bg-primary/10 text-primary border border-primary/20'
                                                            : 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/5'
                                                        }`}>
                                                        {module.status === 'published' ? 'Published' : 'Draft'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => navigate(`/instructor/modules/${module.id}/edit`)}
                                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-900 dark:hover:bg-white hover:text-white dark:hover:text-slate-900 transition-all shrink-0"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                            Edit
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default InstructorModulesPage
